import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, DarkModeProvider, configureApi, initPostHog, posthog } from '@halo/web-lib';
import { PostHogProvider } from 'posthog-js/react';
import * as Sentry from '@sentry/react';
import { App } from './App';
import './index.css';

// ---------- Sentry ----------
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  release: import.meta.env.VITE_BUILD_SHA ?? undefined,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    /AbortError/,
    /AbortController/,
    'Network request failed',
    'Failed to fetch',
    'Load failed',
    /ChunkLoadError/,
    'Loading chunk',
  ],
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
  ],
});

// ---------- PostHog ----------
initPostHog(import.meta.env.VITE_POSTHOG_KEY ?? '', {
  host: import.meta.env.VITE_POSTHOG_HOST,
  app: 'admin',
});

// ---------- API ----------
const gatewayUrl = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:3001';
configureApi(gatewayUrl);

// ---------- React Query ----------
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      Sentry.captureException(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      Sentry.captureException(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ---------- Render ----------
const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <DarkModeProvider>
              <App />
            </DarkModeProvider>
          </AuthProvider>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </BrowserRouter>
    </PostHogProvider>
  </React.StrictMode>,
);
